import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send, Lock, Instagram } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useLang } from "@/i18n";

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const icons = [Mail, Phone, Instagram, MapPin];
const contacts = [
  { value: "nicholoca01@gmail.com", href: "mailto:nicholoca01@gmail.com" },
  { value: "+39 392 637 7344", href: "tel:+393926377344" },
  { value: "@im_nii._", href: "https://instagram.com/im_nii._" },
  { value: null },
];

const inputCls =
  "w-full bg-[#0A0D0B] border border-[#28342C] focus:border-[#C8B48D] outline-none text-[#E6ECE8] placeholder-[#5C6A60] text-sm px-4 py-3.5 transition-colors duration-300";

export const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ nome: "", azienda: "", email: "", telefono: "", tipo_incarico: "", proposta: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setForm((f) => ({ ...f, tipo_incarico: t.contact.types[0] }));
  }, [t]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.email.trim() || !form.proposta.trim()) {
      toast.error(t.contact.toastMissing, { description: t.contact.toastMissingDesc });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setForm({ nome: "", azienda: "", email: "", telefono: "", tipo_incarico: t.contact.types[0], proposta: "" });
      toast.success(t.contact.toastOk, { description: t.contact.toastOkDesc });
    } catch {
      toast.error(t.contact.toastFail, { description: t.contact.toastFailDesc });
    } finally {
      setSending(false);
    }
  };

  const info = contacts.map((c, i) => ({
    icon: icons[i],
    label: t.contact.infoLabels[i],
    value: c.value || t.contact.baseValue,
    href: c.href,
  }));

  return (
    <section id="contatti" className="py-20 lg:py-32 px-4 sm:px-8 lg:px-16" data-testid="contact-section">
      <motion.p
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, ease: EASE }}
        className="font-mono2 text-xs tracking-[0.35em] text-[#C8B48D] mb-4"
      >
        {t.contact.eyebrow}
      </motion.p>
      <motion.h2
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
        className="font-heading text-2xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-wider mb-14"
      >
        {t.contact.titleA} <span className="text-stroke">{t.contact.titleB}</span>
      </motion.h2>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="lg:col-span-5 space-y-4"
        >
          {info.map((c) => {
            const Tag = c.href ? "a" : "div";
            const linkProps = c.href
              ? { href: c.href, ...(c.href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {}) }
              : {};
            return (
            <Tag key={c.label} {...linkProps} className="chamfer-sm border border-[#28342C] bg-[#111613] px-5 py-5 flex items-center gap-4 hover:border-[#5B7052] transition-colors duration-300" data-testid={`contact-info-${c.label.split(" ")[0].toLowerCase()}`}>
              <c.icon className="w-5 h-5 text-[#C8B48D] shrink-0" />
              <div>
                <div className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-[#5C6A60]">{c.label}</div>
                <div className="text-sm sm:text-base text-[#E6ECE8] mt-0.5">{c.value}</div>
              </div>
            </Tag>
            );
          })}
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
          className="lg:col-span-7 chamfer border border-[#28342C] bg-[#111613] p-6 sm:p-10"
          data-testid="contact-form"
        >
          <div className="flex items-center gap-2 font-mono2 text-[10px] tracking-[0.3em] text-[#5C6A60] mb-8">
            <Lock className="w-3.5 h-3.5 text-[#22C55E]" /> {t.contact.secure} <span className="blink text-[#22C55E]">●</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input data-testid="contact-name-input" className={inputCls} placeholder={t.contact.phName} value={form.nome} onChange={set("nome")} />
            <input data-testid="contact-company-input" className={inputCls} placeholder={t.contact.phCompany} value={form.azienda} onChange={set("azienda")} />
            <input data-testid="contact-email-input" type="email" className={inputCls} placeholder={t.contact.phEmail} value={form.email} onChange={set("email")} />
            <input data-testid="contact-phone-input" className={inputCls} placeholder={t.contact.phPhone} value={form.telefono} onChange={set("telefono")} />
          </div>

          <div className="mt-4" data-testid="contact-type-select-wrapper">
            <div className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-[#5C6A60] mb-2">{t.contact.typeLabel}</div>
            <div className="flex flex-wrap gap-2">
              {t.contact.types.map((type) => (
                <button
                  type="button"
                  key={type}
                  data-testid={`contact-type-${type.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  onClick={() => setForm({ ...form, tipo_incarico: type })}
                  className={`font-mono2 text-[10px] tracking-widest uppercase px-3 py-2 border transition-all duration-300 ${
                    form.tipo_incarico === type
                      ? "border-[#C8B48D] bg-[#C8B48D] text-[#0A0D0B]"
                      : "border-[#28342C] text-[#9EACA2] hover:border-[#5B7052]"
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          <textarea
            data-testid="contact-message-input"
            className={`${inputCls} mt-4 min-h-[140px] resize-y`}
            placeholder={t.contact.phMessage}
            value={form.proposta}
            onChange={set("proposta")}
          />

          <button
            type="submit"
            data-testid="submit-offer-button"
            disabled={sending}
            className="chamfer-sm mt-6 w-full sm:w-auto flex items-center justify-center gap-3 bg-[#C8B48D] text-[#0A0D0B] font-mono2 text-xs font-bold tracking-widest px-8 py-4 hover:bg-[#E6ECE8] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
            {sending ? t.contact.sending : t.contact.submit}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
